import { useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { documentApi, transactionApi, invoiceApi, billApi, purchaseOrderApi } from '@/services/api'
import { useClientStore } from '@/stores/clientStore'
import type { Document, Transaction, Journal, Invoice, Bill, PurchaseOrder } from '@/types'
import { FolderOpen, Upload, Trash2, FileText, Image, File, Pencil, Link2, X, Inbox, FolderCheck, Archive, Mail, Copy, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Button, buttonVariants } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const CATEGORIES = ['invoice', 'receipt', 'contract', 'statement', 'other']

const MAX_SIZE = 10 * 1024 * 1024 // 10 MB
const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
]

type TabKey = 'inbox' | 'filed' | 'all'

function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith('image/')) return <Image className="w-4 h-4 text-blue-400" />
  if (mimeType === 'application/pdf') return <FileText className="w-4 h-4 text-red-400" />
  return <File className="w-4 h-4 text-[#CCCED2]" />
}

function formatSize(bytes: number | null) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function linkedLabel(doc: Document): string | null {
  if (doc.linked_invoice_id) return 'Invoice'
  if (doc.linked_bill_id) return 'Bill'
  if (doc.linked_purchase_order_id) return 'Purchase Order'
  if (doc.linked_transaction_id) return 'Transaction'
  if (doc.linked_journal_id) return 'Journal'
  return null
}

const STATUS_BADGE: Record<string, { label: string; variant: 'warning' | 'default' | 'success' }> = {
  inbox: { label: 'Inbox', variant: 'warning' },
  filed: { label: 'Filed', variant: 'default' },
  linked: { label: 'Linked', variant: 'success' },
}

export default function DocumentLibrary() {
  const { clientId } = useParams<{ clientId: string }>()
  const currentClient = useClientStore((s) => s.currentClient)
  const [docs, setDocs] = useState<Document[]>([])
  const [inboxCount, setInboxCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState('')
  const [activeTab, setActiveTab] = useState<TabKey>('inbox')
  const [copied, setCopied] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Edit dialog state
  const [editTarget, setEditTarget] = useState<Document | null>(null)
  const [editCategory, setEditCategory] = useState('')
  const [saving, setSaving] = useState(false)

  // Link dialog state
  const [linkTarget, setLinkTarget] = useState<Document | null>(null)
  const [linkType, setLinkType] = useState<'transaction' | 'journal' | 'invoice' | 'bill' | 'purchase_order'>('transaction')
  const [linkEntityId, setLinkEntityId] = useState('')
  const [linking, setLinking] = useState(false)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [journals, setJournals] = useState<Journal[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [bills, setBills] = useState<Bill[]>([])
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([])
  const [isPremium, setIsPremium] = useState(false)
  const [entitiesLoaded, setEntitiesLoaded] = useState(false)

  useEffect(() => {
    loadDocs()
  }, [clientId, categoryFilter, activeTab])

  function loadDocs() {
    if (!clientId) return
    setLoading(true)
    const params: Record<string, string> = {}
    if (categoryFilter) params.category = categoryFilter
    if (activeTab !== 'all') params.status = activeTab === 'filed' ? 'filed' : 'inbox'
    documentApi
      .list(clientId, params)
      .then((res) => {
        setDocs(res.data.documents)
        setInboxCount(res.data.inbox_count ?? 0)
      })
      .catch(() => toast.error('Failed to load documents'))
      .finally(() => setLoading(false))
  }

  function loadEntities() {
    if (entitiesLoaded || !clientId) return
    Promise.all([
      transactionApi.list(clientId),
      transactionApi.listJournals(clientId),
    ]).then(([txnRes, jrnRes]) => {
      setTransactions(txnRes.data.transactions)
      setJournals(jrnRes.data.journals)
    }).catch(() => {})

    invoiceApi.list(clientId).then((res) => {
      setInvoices(res.data.invoices)
      setIsPremium(true)
    }).catch(() => {})

    billApi.list(clientId).then((res) => {
      setBills(res.data.bills)
    }).catch(() => {})

    purchaseOrderApi.list(clientId).then((res) => {
      setPurchaseOrders(res.data.purchase_orders)
    }).catch(() => {})

    setEntitiesLoaded(true)
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !clientId) return

    if (file.size > MAX_SIZE) {
      toast.error('File exceeds 10 MB limit')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('File type not supported')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setUploading(true)
    try {
      const res = await documentApi.upload(clientId, file, categoryFilter)
      // New uploads land in inbox — if we're on inbox or all tab, add to list
      if (activeTab === 'inbox' || activeTab === 'all') {
        setDocs((d) => [res.data, ...d])
      }
      setInboxCount((c) => c + 1)
      toast.success('Document uploaded to Inbox')
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function confirmDelete() {
    if (!deleteTarget || !clientId) return
    setDeleting(true)
    try {
      await documentApi.delete(clientId, deleteTarget.id)
      const wasInbox = deleteTarget.doc_status === 'inbox'
      setDocs((d) => d.filter((x) => x.id !== deleteTarget.id))
      if (wasInbox) setInboxCount((c) => Math.max(0, c - 1))
      toast.success('Document deleted')
      setDeleteTarget(null)
    } catch {
      toast.error('Failed to delete')
    } finally {
      setDeleting(false)
    }
  }

  function openEdit(doc: Document) {
    setEditTarget(doc)
    setEditCategory(doc.category || '')
  }

  async function confirmEdit() {
    if (!editTarget || !clientId) return
    setSaving(true)
    try {
      const res = await documentApi.patch(clientId, editTarget.id, { category: editCategory })
      setDocs((d) => d.map((x) => (x.id === editTarget.id ? res.data : x)))
      toast.success('Document updated')
      setEditTarget(null)
    } catch {
      toast.error('Failed to update')
    } finally {
      setSaving(false)
    }
  }

  async function fileDoc(doc: Document) {
    if (!clientId) return
    try {
      const res = await documentApi.patch(clientId, doc.id, { doc_status: 'filed' })
      // Update in-place or remove from inbox view
      if (activeTab === 'inbox') {
        setDocs((d) => d.filter((x) => x.id !== doc.id))
      } else {
        setDocs((d) => d.map((x) => (x.id === doc.id ? res.data : x)))
      }
      setInboxCount((c) => Math.max(0, c - 1))
      toast.success('Moved to Filed')
    } catch {
      toast.error('Failed to file document')
    }
  }

  function openLink(doc: Document) {
    setLinkTarget(doc)
    if (doc.linked_invoice_id) {
      setLinkType('invoice')
      setLinkEntityId(doc.linked_invoice_id)
    } else if (doc.linked_bill_id) {
      setLinkType('bill')
      setLinkEntityId(doc.linked_bill_id)
    } else if (doc.linked_purchase_order_id) {
      setLinkType('purchase_order')
      setLinkEntityId(doc.linked_purchase_order_id)
    } else if (doc.linked_journal_id) {
      setLinkType('journal')
      setLinkEntityId(doc.linked_journal_id)
    } else if (doc.linked_transaction_id) {
      setLinkType('transaction')
      setLinkEntityId(doc.linked_transaction_id)
    } else {
      setLinkType('transaction')
      setLinkEntityId('')
    }
    loadEntities()
  }

  async function confirmLink() {
    if (!linkTarget || !clientId || !linkEntityId) return
    setLinking(true)
    const patch: Record<string, string | null> = {
      linked_transaction_id: null,
      linked_journal_id: null,
      linked_invoice_id: null,
      linked_bill_id: null,
      linked_purchase_order_id: null,
    }
    if (linkType === 'transaction') patch.linked_transaction_id = linkEntityId
    else if (linkType === 'journal') patch.linked_journal_id = linkEntityId
    else if (linkType === 'invoice') patch.linked_invoice_id = linkEntityId
    else if (linkType === 'bill') patch.linked_bill_id = linkEntityId
    else if (linkType === 'purchase_order') patch.linked_purchase_order_id = linkEntityId

    try {
      const res = await documentApi.patch(clientId, linkTarget.id, patch)
      const wasInbox = linkTarget.doc_status === 'inbox'
      // If on inbox tab, the doc is now linked — remove from view
      if (activeTab === 'inbox') {
        setDocs((d) => d.filter((x) => x.id !== linkTarget.id))
      } else {
        setDocs((d) => d.map((x) => (x.id === linkTarget.id ? res.data : x)))
      }
      if (wasInbox) setInboxCount((c) => Math.max(0, c - 1))
      toast.success('Document linked')
      setLinkTarget(null)
    } catch {
      toast.error('Failed to link document')
    } finally {
      setLinking(false)
    }
  }

  async function unlinkDoc(doc: Document) {
    if (!clientId) return
    try {
      const res = await documentApi.patch(clientId, doc.id, {
        linked_transaction_id: '',
        linked_journal_id: '',
        linked_invoice_id: '',
        linked_bill_id: '',
        linked_purchase_order_id: '',
      })
      // Unlinking moves back to inbox — update or remove depending on tab
      if (activeTab === 'inbox') {
        // Shouldn't be on inbox tab with a linked doc, but just in case
        setDocs((d) => d.map((x) => (x.id === doc.id ? res.data : x)))
      } else if (activeTab === 'filed') {
        // Now it's inbox again, remove from filed view
        setDocs((d) => d.filter((x) => x.id !== doc.id))
      } else {
        setDocs((d) => d.map((x) => (x.id === doc.id ? res.data : x)))
      }
      setInboxCount((c) => c + 1)
      toast.success('Link removed — moved to Inbox')
    } catch {
      toast.error('Failed to unlink')
    }
  }

  const entityOptions =
    linkType === 'transaction'
      ? transactions.map((t) => ({ id: t.id, label: `${t.date} — ${t.description || t.reference || 'No description'}` }))
      : linkType === 'journal'
        ? journals.map((j) => ({ id: j.id, label: `${j.date} — ${j.narration || 'No narration'}` }))
        : linkType === 'invoice'
          ? invoices.map((i) => ({ id: i.id, label: `${i.invoice_number} — ${i.contact_name} (${i.total})` }))
          : linkType === 'bill'
            ? bills.map((b) => ({ id: b.id, label: `${b.bill_number} — ${b.supplier_name} (${b.total})` }))
            : purchaseOrders.map((p) => ({ id: p.id, label: `${p.po_number} — ${p.supplier_name} (${p.total})` }))

  const tabs: { key: TabKey; label: string; icon: typeof Inbox; count?: number }[] = [
    { key: 'inbox', label: 'Inbox', icon: Inbox, count: inboxCount },
    { key: 'filed', label: 'Filed', icon: Archive },
    { key: 'all', label: 'All Documents', icon: FolderOpen },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Documents</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">File library for this client</p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v ?? '')}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All categories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <label
            className={cn(
              buttonVariants({ variant: 'create' }),
              uploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer',
              'gap-1.5',
            )}
          >
            <Upload className="w-4 h-4" />
            {uploading ? 'Uploading...' : 'Upload File'}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
              accept={ALLOWED_TYPES.join(',')}
            />
          </label>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'flex items-center gap-1.5 px-4 py-3 text-[13px] -mb-[2px] border-b-[3px] transition-colors',
              activeTab === tab.key
                ? 'text-[#000A1E] font-bold border-b-[#0078C8]'
                : 'text-[#0078C8] font-normal border-b-transparent',
            )}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={cn(
                'text-[10px] font-semibold rounded-full px-1.5 py-0.5 leading-none',
                activeTab === tab.key
                  ? 'bg-[#0078C8] text-white'
                  : 'bg-amber-100 text-amber-700',
              )}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Forwarding email banner — Inbox tab only */}
      {activeTab === 'inbox' && currentClient?.inbound_email && (
        <div className="flex items-center gap-3 rounded-lg border border-blue-100 bg-blue-50/50 px-4 py-3">
          <Mail className="w-4 h-4 text-blue-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-blue-600 font-medium">Forward supplier invoices to:</p>
            <p className="text-sm font-mono text-blue-800 truncate">{currentClient.inbound_email}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 gap-1.5 text-xs"
            onClick={() => {
              navigator.clipboard.writeText(currentClient.inbound_email)
              setCopied(true)
              toast.success('Email address copied')
              setTimeout(() => setCopied(false), 2000)
            }}
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : docs.length === 0 ? (
            <div className="py-16 text-center">
              {activeTab === 'inbox' ? (
                <>
                  <Inbox className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
                  <p className="text-[13px] font-medium text-[#000A1E] mb-1">Inbox is empty</p>
                  <p className="text-[13px] text-[rgba(0,10,30,0.75)]">Upload documents — they'll land here for processing</p>
                </>
              ) : activeTab === 'filed' ? (
                <>
                  <Archive className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
                  <p className="text-[13px] font-medium text-[#000A1E] mb-1">No filed documents</p>
                  <p className="text-[13px] text-[rgba(0,10,30,0.75)]">File documents from the Inbox to move them here</p>
                </>
              ) : (
                <>
                  <FolderOpen className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
                  <p className="text-[13px] text-[rgba(0,10,30,0.75)]">No documents yet</p>
                  <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-1">Upload a file to get started</p>
                </>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>File</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Linked To</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="w-36" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((doc) => {
                  const link = linkedLabel(doc)
                  const badge = STATUS_BADGE[doc.doc_status] || STATUS_BADGE.inbox
                  return (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <FileIcon mimeType={doc.mime_type} />
                      </TableCell>
                      <TableCell>
                        <a
                          href={doc.file_path}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[13px] font-bold text-[#0078C8] hover:underline truncate block max-w-xs"
                        >
                          {doc.file_name}
                        </a>
                      </TableCell>
                      <TableCell>
                        {doc.category ? (
                          <Badge variant="default" className="capitalize">
                            {doc.category}
                          </Badge>
                        ) : (
                          <span className="text-[13px] text-[#CCCED2]">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={badge.variant}>
                          {badge.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {link ? (
                          <div className="flex items-center gap-1.5">
                            <Badge variant="info">
                              {link}
                            </Badge>
                            <button
                              onClick={() => unlinkDoc(doc)}
                              className="text-[#CCCED2] hover:text-red-500 transition-colors"
                              title="Remove link"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-[13px] text-[#CCCED2]">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[13px] text-[rgba(0,10,30,0.75)]">
                        {formatSize(doc.file_size)}
                      </TableCell>
                      <TableCell className="text-[13px] text-[rgba(0,10,30,0.75)]">
                        {new Date(doc.uploaded_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 justify-end">
                          {doc.doc_status === 'inbox' && (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => fileDoc(doc)}
                              title="File (move out of inbox)"
                            >
                              <FolderCheck className="w-3.5 h-3.5 text-[rgba(0,10,30,0.75)]" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => openLink(doc)}
                            title="Link to transaction or journal"
                          >
                            <Link2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => openEdit(doc)}
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-red-400 hover:text-red-600"
                            onClick={() => setDeleteTarget(doc)}
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete document?</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)]">
            <span className="font-medium text-[#000A1E]">{deleteTarget?.file_name}</span> will be
            permanently deleted and cannot be recovered.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => { if (!open) setEditTarget(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit document</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-[13px] text-[rgba(0,10,30,0.75)] font-medium">{editTarget?.file_name}</p>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={editCategory} onValueChange={(v) => setEditCategory(v ?? '')}>
                <SelectTrigger>
                  <SelectValue placeholder="No category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No category</SelectItem>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c.charAt(0).toUpperCase() + c.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={confirmEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link dialog */}
      <Dialog open={!!linkTarget} onOpenChange={(open) => { if (!open) setLinkTarget(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-[13px] text-[rgba(0,10,30,0.75)]">
              Link <span className="font-medium text-[#000A1E]">{linkTarget?.file_name}</span> to a transaction or journal entry.
            </p>
            <div className="space-y-1.5">
              <Label>Link to</Label>
              <Select value={linkType} onValueChange={(v) => { setLinkType(v as 'transaction' | 'journal' | 'invoice' | 'bill' | 'purchase_order'); setLinkEntityId('') }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transaction">Transaction</SelectItem>
                  <SelectItem value="journal">Journal Entry</SelectItem>
                  {isPremium && <SelectItem value="invoice">Invoice</SelectItem>}
                  {isPremium && <SelectItem value="bill">Bill</SelectItem>}
                  {isPremium && <SelectItem value="purchase_order">Purchase Order</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{{ transaction: 'Transaction', journal: 'Journal', invoice: 'Invoice', bill: 'Bill', purchase_order: 'Purchase Order' }[linkType]}</Label>
              <select
                value={linkEntityId}
                onChange={(e) => setLinkEntityId(e.target.value)}
                className="input"
              >
                <option value="">Select...</option>
                {entityOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkTarget(null)} disabled={linking}>
              Cancel
            </Button>
            <Button onClick={confirmLink} disabled={linking || !linkEntityId}>
              {linking ? 'Linking...' : 'Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
